import { useCallback } from "react"
import { FormProvider, useForm, useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { useDirtyDrawerMaker } from "@/components/drawer-wrapper"
import { ScrollWrapper } from "@/components/scroll-wrapper"
import { Button } from "@/components/ui/button"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { CommentTextArea } from "@/modules/patients/detail/health-information/comment-text-area"
import { ConfirmedBy } from "@/modules/patients/detail/health-information/confirmed-by"
import { ReadOnlyFormField } from "@/modules/patients/detail/health-information/readonly-form-field"
import { SelectFormField } from "@/modules/patients/detail/health-information/select-form-field"
import { useEditMode } from "@/modules/patients/detail/hooks/useEditMode"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import {
  AutoimmuneDisease,
  Cancer,
  Diabetes,
  FamilyHistoryFragment$key,
  HeartDisease,
} from "@/modules/patients/services/__generated__/FamilyHistoryFragment.graphql"
import { Ch_updatePatientFamilyHistoryInput } from "@/modules/patients/services/ch/__generated__/chUpdatePatientFamilyHistoryMutation.graphql"
import { Ch_verifyHealthInformationInput } from "@/modules/patients/services/ch/__generated__/chVerifyHealthInformationMutation.graphql"
import { De_updatePatientFamilyHistoryInput } from "@/modules/patients/services/de/__generated__/deUpdatePatientFamilyHistoryMutation.graphql"
import { De_verifyHealthInformationInput } from "@/modules/patients/services/de/__generated__/deVerifyHealthInformationMutation.graphql"
import { FamilyHistoryFragment } from "@/modules/patients/services/FamilyHistoryFragment"
import { usePatientFamilyHistory } from "@/modules/patients/services/usePatientFamilyHistory"
import { useUpdatePatientFamilyHistoryMutation } from "@/modules/patients/services/useUpdatePatientFamilyHistoryMutation"
import { useVerifyHealthInformation } from "@/modules/patients/services/useVerifyHealthInformation"
import { useDrawerStore } from "@/stores/useDrawerStore"

const HEART_DISEASE_OPTIONS: HeartDisease[] = ["YES", "NO", "UNKNOWN", "NOT_SPECIFIED"]
const DIABETES_OPTIONS: Diabetes[] = [
  "YES_DIABETES_TYPE_ONE",
  "YES_DIABETES_TYPE_TWO",
  "YES_DIABETES_TYPE_ONE_AND_TWO",
  "NO",
  "UNKNOWN",
  "NOT_SPECIFIED",
]
const CANCER_OPTIONS: Cancer[] = ["YES", "NO", "UNKNOWN", "NOT_SPECIFIED"]
const AUTOIMMUNE_DISEASE_OPTIONS: AutoimmuneDisease[] = ["YES", "NO", "UNKNOWN", "NOT_SPECIFIED"]

const familyHistorySchema = z.object({
  comment: z.string().nullable(),
  heartDisease: z.custom<HeartDisease>(),
  diabetes: z.custom<Diabetes>(),
  cancer: z.custom<Cancer>(),
  autoimmuneDisease: z.custom<AutoimmuneDisease>(),
})

type FamilyHistoryForm = z.infer<typeof familyHistorySchema>

const FamilyHistoryDrawer = ({
  canVerify,
  verifiedBy,
  verifiedAt,
}: {
  canVerify: boolean
  verifiedBy: string
  verifiedAt: string
}) => {
  const isEditMode = useEditMode()
  const { t } = useTranslation()
  const closeDrawer = useDrawerStore((state) => state.closeDrawer)
  const patientId = usePatientParam()
  const appointmentId = useAppointmentParam()
  const country = useCountryParam()
  const handleUpdateFamilyHistory = useUpdatePatientFamilyHistoryMutation(country)
  const handleVerifyHealthInformation = useVerifyHealthInformation(country)
  const dataRef = usePatientFamilyHistory(patientId, country)
  const data = useFragment<FamilyHistoryFragment$key>(FamilyHistoryFragment, dataRef)
  const { comment, heartDisease, diabetes, cancer, autoimmuneDisease } = data

  const form = useForm<FamilyHistoryForm>({
    resolver: zodResolver(familyHistorySchema),
    defaultValues: {
      comment,
      heartDisease,
      diabetes,
      cancer,
      autoimmuneDisease,
    },
  })
  const {
    handleSubmit,
    formState: { isDirty },
  } = form

  const handleVerifyFamilyHistory = () => {
    const verification: De_verifyHealthInformationInput & Ch_verifyHealthInformationInput = {
      patientId,
      healthInformationType: "FAMILY_HISTORY",
      appointmentId: appointmentId!,
    }

    handleVerifyHealthInformation(verification, closeDrawer)
  }

  const handleSaveFamilyHistory = (data: FamilyHistoryForm) => {
    if (isDirty) {
      const submitData: De_updatePatientFamilyHistoryInput & Ch_updatePatientFamilyHistoryInput = {
        autoimmuneDisease: data.autoimmuneDisease,
        cancer: data.cancer,
        comment: data.comment,
        diabetes: data.diabetes,
        heartDisease: data.heartDisease,
        patientId,
      }
      handleUpdateFamilyHistory(submitData, closeDrawer)
    } else {
      closeDrawer()
    }
  }

  const handleClose = useCallback(() => {
    FaroInsightsWrapper.pushEvent("healthInformation_family_close")
    closeDrawer()
  }, [closeDrawer])

  useDirtyDrawerMaker(isDirty, handleClose)

  return (
    <Drawer open onOpenChange={handleClose}>
      <DrawerContent data-testid="family-history-drawer-content">
        <DrawerClose className="top-2 right-2" />
        <DrawerHeader className="border-b py-2">
          <DrawerTitle
            data-testid="family-history-drawer-title"
            className="animate-fade-in-bottom text-on-surface-subdued text-sm leading-10 font-medium">
            {t("patient.healthInformation.familyHistory.title")}
          </DrawerTitle>
        </DrawerHeader>
        <DrawerBody className="p-0">
          <ScrollWrapper className="p-4">
            <div className="flex flex-col gap-8">
              <FormProvider {...form}>
                {isEditMode ? <FamilyHistoryEditableBody /> : <FamilyHistoryReadonlyBody />}
              </FormProvider>
            </div>
          </ScrollWrapper>
        </DrawerBody>
        <DrawerFooter className="justify-end gap-2 overflow-hidden">
          {verifiedBy !== "" && <ConfirmedBy verifiedBy={verifiedBy} verifiedAt={verifiedAt} />}
          <Button data-testid="family-history-drawer-close-cancel-button" variant="secondary" onClick={handleClose}>
            {isEditMode ? t("common.button.cancel") : t("common.button.close")}
          </Button>
          {isEditMode && !canVerify && (
            <Button
              data-testid="family-history-drawer-save-button"
              onClick={() => {
                FaroInsightsWrapper.pushEvent("healthInformation_family_save")
                handleSubmit(handleSaveFamilyHistory)()
              }}>
              {t("common.button.save")}
            </Button>
          )}
          {isEditMode && canVerify && (
            <Button
              data-testid="family-history-drawer-save-button"
              onClick={() => {
                handleSubmit(handleSaveFamilyHistory)()
                handleVerifyFamilyHistory()
              }}>
              {t("common.button.verify")}
            </Button>
          )}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}

const FamilyHistoryEditableBody = () => {
  const { t } = useTranslation()

  return (
    <>
      <CommentTextArea data-testid="family-history-drawer-comment" fieldName="comment" maxLength={200} />
      <div className="flex flex-col gap-4">
        <span data-testid="medical-history-title-editable-mode" className="text-on-surface text-base font-medium">
          {t("patient.healthInformation.familyHistory.familyMedicalHistory")}
        </span>
        <SelectFormField
          fieldName="heartDisease"
          label={t("patient.healthInformation.familyHistory.heartDisease.label")}
          options={HEART_DISEASE_OPTIONS.map((option) => ({
            label: t(`patient.healthInformation.familyHistory.heartDisease.options.${option}`),
            value: option,
          }))}
        />
        <SelectFormField
          fieldName="diabetes"
          label={t("patient.healthInformation.familyHistory.diabetes.label")}
          options={DIABETES_OPTIONS.map((option) => ({
            label: t(`patient.healthInformation.familyHistory.diabetes.options.${option}`),
            value: option,
          }))}
        />
        <SelectFormField
          fieldName="cancer"
          label={t("patient.healthInformation.familyHistory.cancer.label")}
          options={CANCER_OPTIONS.map((option) => ({
            label: t(`patient.healthInformation.familyHistory.cancer.options.${option}`),
            value: option,
          }))}
        />
        <SelectFormField
          fieldName="autoimmuneDisease"
          label={t("patient.healthInformation.familyHistory.autoimmuneDisease.label")}
          options={AUTOIMMUNE_DISEASE_OPTIONS.map((option) => ({
            label: t(`patient.healthInformation.familyHistory.autoimmuneDisease.options.${option}`),
            value: option,
          }))}
        />
      </div>
    </>
  )
}

const FamilyHistoryReadonlyBody = () => {
  const { t } = useTranslation()
  const { getValues } = useFormContext()

  return (
    <>
      <ReadOnlyFormField dataTestId="family-history-drawer-comment-view-mode" label={t("common.comment")}>
        <CommentTextArea isEditMode={false} fieldName="comment" maxLength={200} />
      </ReadOnlyFormField>
      <div className="flex flex-col gap-4">
        <span data-testid="family-medical-history-view-mode" className="text-on-surface text-base font-medium">
          {t("patient.healthInformation.familyHistory.familyMedicalHistory")}
        </span>
        <ReadOnlyFormField
          dataTestId="heart-disease-view-mode"
          label={t("patient.healthInformation.familyHistory.heartDisease.label")}>
          {t(`patient.healthInformation.familyHistory.heartDisease.options.${getValues("heartDisease")}`)}
        </ReadOnlyFormField>
        <ReadOnlyFormField
          dataTestId="diabetes-view-mode"
          label={t("patient.healthInformation.familyHistory.diabetes.label")}>
          {t(`patient.healthInformation.familyHistory.diabetes.options.${getValues("diabetes")}`)}
        </ReadOnlyFormField>
        <ReadOnlyFormField
          dataTestId="cancer-view-mode"
          label={t("patient.healthInformation.familyHistory.cancer.label")}>
          {t(`patient.healthInformation.familyHistory.cancer.options.${getValues("cancer")}`)}
        </ReadOnlyFormField>
        <ReadOnlyFormField
          dataTestId="autoimmune-disease-view-mode"
          label={t("patient.healthInformation.familyHistory.autoimmuneDisease.label")}>
          {t(`patient.healthInformation.familyHistory.autoimmuneDisease.options.${getValues("autoimmuneDisease")}`)}
        </ReadOnlyFormField>
      </div>
    </>
  )
}

export { FamilyHistoryDrawer }
